import React, { useState } from 'react';
import SurveyRecordsTable from '../SurveyRecordsTable';
import { EnhanceTableAction, IField, IPatient, ISubmission, ISurvey } from '../../../constants/types';
import { Translate } from 'react-localize-redux';
import { CATEGORY_DEPARTMENT_PRESCRIPTIONS } from '../../../constants';
import * as types from "../../../constants";
import Modal from '../../../components/general/modal';
import { Typography } from '@mui/material';
import { HOSPITAL_BILLING_EDIT } from '../../../routes/urls';
import { LinkStyled } from '../../../components/general/mini_components';
import { Link } from 'react-router-dom';
import { BillingInfo } from '../Billing/types';
import SubmissionsTableSelector from './SubmissionsTableSelector';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { SubmissionPDFDocument } from '../../../components/pdf/documents/SubmissionPDFDocument';
import { PDFDownloadButtonWithSignature } from '../../../components/pdf/components/PDFDownloadButtonWithSignature';
import { usePDFConfig } from '../../../hooks/usePDFConfig';
import { useSelector } from 'react-redux';
import { extractPrescribingDoctor } from '../../../utils/doctorUtils';
import { useResearcherRole } from '../../../hooks/useResearcherRole';

interface SubmissionsTableProps {
    locale:string,
    fieldsSurvey?: IField[],
    surveys: ISurvey[],
    records: ISubmission[],
    category: number,
    patient:IPatient,
    billingInfo: BillingInfo | null,
    onSelectSubmission: (index:number) => void;
}

const SubmissionsTable: React.FC<SubmissionsTableProps> = ({ locale, surveys, patient, billingInfo, category, fieldsSurvey, records, 
                                                        onSelectSubmission }) => {
    const [showModalPrintPDF, setShowModalPrintPDF] = useState(false);
    const [submissionPrint, setSubmissionPrint] = useState<ISubmission | null>(null);
    
    // Get PDF config and investigation UUID for new PDF component
    const { config: pdfConfig } = usePDFConfig();
    const currentInvestigationUuid = useSelector((state: any) =>
        state.investigations.currentInvestigation?.uuid
    );
    // SHER-224: resolve the uploading researcher's role to label the PDF signature.
    const prescribingDoctorRole = useResearcherRole({
        uuidInvestigation: currentInvestigationUuid || '',
        uuidResearcher: submissionPrint?.uuidResearcher,
        enabled: showModalPrintPDF,
    });

    function printSubmission(submission:ISubmission){
        setSubmissionPrint(submission);
        setShowModalPrintPDF(true);
    }
    function renderPDFModal(){
        const currentSurvey = surveys.find((survey) => survey.uuid === submissionPrint?.uuidSurvey);
        
        if(!pdfConfig){
            return(
                <Modal key="modal" medium open={showModalPrintPDF} title="" closeModal={() => setShowModalPrintPDF(false)}>
                    <Typography variant='body2'><Translate id="pages.hospital.patient.fill_billing_info" />, <Link to={HOSPITAL_BILLING_EDIT}><Translate id="general.here" /></Link></Typography>
                </Modal>
            )
        }
        else if(currentSurvey && submissionPrint){
            // Find prescribing doctor from the submission
            const prescribingDoctor = extractPrescribingDoctor(submissionPrint);
            
            return(
                <Modal key="modal" medium open={showModalPrintPDF} title="" closeModal={() => setShowModalPrintPDF(false)}>
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <PDFDownloadButtonWithSignature
                            uuidInvestigation={currentInvestigationUuid || ''}
                            uuidResearcher={prescribingDoctor?.uuid}
                            filename={`${currentSurvey.name}-${submissionPrint.id}`}
                            variant="contained"
                            size="large"
                            label={<Translate id="general.download_pdf" />}
                        >
                            {(signatureBase64) => (
                                <SubmissionPDFDocument
                                    submission={submissionPrint}
                                    currentSurvey={currentSurvey}
                                    patient={patient}
                                    prescribingDoctor={prescribingDoctor}
                                    prescribingDoctorRole={prescribingDoctorRole || undefined}
                                    config={pdfConfig}
                                    nameSurvey={currentSurvey.name}
                                    locale={locale}
                                    uuidInvestigation={currentInvestigationUuid || ''}
                                    signatureBase64={signatureBase64}
                                />
                            )}
                        </PDFDownloadButtonWithSignature>
                    </div>
                </Modal>
            )
        }
        
    }
    if(surveys.length > 0 && [types.CATEGORY_DEPARTMENT_NURSE, types.CATEGORY_DEPARTMENT_NURSE_FW].includes(surveys[0].category)){
        return <SubmissionsTableSelector surveys={surveys} records={records} 
                    locale={locale} billingInfo={billingInfo}
                    onSelectSubmission={(index) => onSelectSubmission(index)}  />
    }
        
    let headCells = [{ id: "researcher", alignment: "left", label: <Translate id="hospital.created-by" />}, { id: "surveyName", alignment: "left", label: <Translate id="hospital.data-collection" />},
                        { id: "date", alignment: "left", label: "Date"}];
    let actions:EnhanceTableAction[] = [
        {
            "type" : "pdf", 
            "func" : (index) => printSubmission(records[index]),
            "check": (row: any) => {
                const record = records[row.id];
                return record && (record as any).type !== "stay";
            }
        }
    ];
    return(
        <>
            {renderPDFModal()}
            <EnhancedTable noHeader noSelectable selectRow={(index:number) => onSelectSubmission(index)} 
                rows={records.map((record, index) => {
                    const dateCreatedString = record.createdAt ? new Date(record.createdAt).toLocaleString('en-CA', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    }).replace(',', '') : "Unsincronized";
                    return({id : index, researcher : record.researcher, surveyName : record.surveyName, date : dateCreatedString, type: (record as any).type})
                })} headCells={headCells} 
                    actions={actions} /> 
        </>
    );
};

export default SubmissionsTable;
